<template>
  <div>
    <app-nav />
    <div class="container-fluid">
      <div class="columns">
        <div class="column is-10">
          <span class="title">{{ app.name }}</span>
        </div>
        <div class="column is-2 has-text-right">
          <button
            class="button primary create-update-btn"
            @click="openCreateModal()"
          >
            {{ $t('api_keys.info.create_api_key') }}
          </button>
        </div>
      </div>
      <div>
        <div class="keys-wrapper">
          <div class="keys-tabs">
            <ul>
              <li
                v-for="(val, index) in tabs.values"
                :key="index"
                :class="{ 'is-active': tabs.activeTab === val }"
              >
                <a @click="activateTab(val)">
                  <span>{{ tabs.labels[val] }}</span>
                </a>
              </li>
            </ul>
          </div>
          <div class="table-container">
            <div v-for="(val, index) in tabs.values" :class="val" :key="index">
              <spinner class="animated" v-if="isLoading"></spinner>
              <div v-else>
                <div v-if="val === tabs.activeTab">
                  <div v-if="keys[val].length">
                    <div class="columns table-header">
                      <key-header :current-tab="tabs.activeTab" />
                    </div>
                    <div
                      v-for="(value, i) in keys[val]"
                      :key="i"
                      class="columns table-values"
                    >
                      <key-row
                        :data="value"
                        :current-tab="tabs.activeTab"
                        @open-revoke-modal="openRevokeModal"
                      />
                    </div>
                  </div>
                  <div v-else class="no-content">
                    <p>{{ $t('api_keys.info.empty_tab') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <key-create-modal
      :is-active="keyCreateModal.isActive"
      @close-modal="closeCreateModal"
      @reload="reload"
    />
    <key-revoke-modal
      :is-active="keyRevokeModal.isActive"
      @close-modal="closeRevokeModal"
      @reload="reload"
    />
  </div>
</template>

<script>
import store from 'store';
import { mapGetters, mapState } from 'vuex';
import notifications from 'services/Notifications';
import AppNav from 'views/boards/common/modules/AppNav.vue';
import KeyRow from './modules/KeyRow.vue';
import KeyHeader from './modules/KeyHeader.vue';
import KeyCreateModal from './modules/KeyCreateModal.vue';
import KeyRevokeModal from './modules/KeyRevokeModal.vue';
import { Spinner } from 'components/loaders';
import i18n from 'services/i18n';
import router from 'router';

const ALLOWED_TABS = ['active', 'expired', 'revoked'];

function _getActiveTab(route) {
  return ALLOWED_TABS.includes(route.query.tab) ? route.query.tab : 'active';
}

export default {
  name: 'ApiKeyBoard',
  beforeRouteEnter(to, from, next) {
    store
      .dispatch('application/loadApp', to.params.appId)
      .then(() => {
        return store.commit('api_key/resetState');
      })
      .then(() => {
        const appId = to.params.appId;
        const app = store.getters['application/getApp'](appId);

        store.commit('api_key/setCurrentAppId', to.params.appId);
        return store.commit('api_key/setCurrentAccountId', app.account_id);
      })
      .then(() => {
        return store.dispatch('api_key/getKeysForCurrentAppId', {
          filter: _getActiveTab(to),
        });
      })
      .then(next)
      .catch(err => {
        notifications.add(i18n.t('api_keys.errors.getting_init_data_failed'));
      });
  },
  components: {
    AppNav,
    KeyCreateModal,
    KeyHeader,
    KeyRevokeModal,
    KeyRow,
    Spinner,
  },
  computed: {
    ...mapState('api_key', ['currentAppId', 'errors', 'keys']),
    ...mapGetters('application', ['app']),
  },
  data() {
    return {
      isLoading: false,
      keyCreateModal: {
        isActive: false,
      },
      keyRevokeModal: {
        isActive: false,
      },
      tabs: {
        values: ALLOWED_TABS,
        labels: {
          active: 'Active',
          expired: 'Expired',
          revoked: 'Revoked',
        },
        activeTab: _getActiveTab(router.currentRoute),
      },
    };
  },

  methods: {
    closeCreateModal() {
      this.keyCreateModal.isActive = false;
    },

    closeRevokeModal() {
      this.keyRevokeModal.isActive = false;

      store.commit('api_key/setKeyToRevoke', null);
    },

    reload() {
      this.getApiKeys('active');
    },

    activateTab(tabName) {
      if (tabName !== this.tabs.activeTab) {
        this.tabs.activeTab = tabName;

        router.push({ query: { tab: tabName } });

        this.getApiKeys(tabName);
      }
    },

    async getApiKeys(filter) {
      try {
        this.isLoading = true;
        await store.dispatch('api_key/getKeysForCurrentAppId', { filter });
      } catch (error) {
        console.log(error);

        const message = error.body && error.body.message;
        notifications.add(
          message || this.$t('api_keys.errors.getting_api_keys_failed')
        );
      } finally {
        this.isLoading = false;
      }
    },

    openCreateModal(type) {
      store.commit('api_key/resetFormErrors');
      store.commit('api_key/resetCreateKey');
      store.commit('api_key/resetValidations');
      store.commit('api_key/resetForm');

      this.keyCreateModal.isActive = true;
    },

    openRevokeModal(type) {
      this.keyRevokeModal.isActive = true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';
.container-fluid {
  padding: 2rem;
}
</style>
